import React, { memo, useCallback, useMemo } from 'react';

import PortfolioTable from './ComposizioneDelPortafoglioTable/ComposizioneDelPortafoglioTable';

import colorsStore from 'shared/store/colorsStore';
import { formatItalianNumber } from 'utils/formatUtils';
import styles from './ComposizioneDelPortafoglio.module.scss';
import { CountervalueData, ProductData } from './types';
import { hideGlobalTooltip, showGlobalTooltip } from './utils';
import GlobalTooltip from './GlobalTooltip';
import useStrumentiImportedSort from './hooks/useStrumentiImportedSort';
import { HIDDEN_COLUMNS } from 'config/portfolio-hidden-columns';
import { useFeeType } from 'pages/assistant/hooks/useFeeType';
import activityPanelStore from 'shared/store/activityPanelStore';
import { POLIZZE_COLUMN_LABELS, POLIZZE_SUBTOTAL_LABEL, polizzaTipoProdotto, polizzeSubTitle, POLIZZE_TITLE } from './polizze';

// Column widths for fondi
const BASE_COLUMN_WIDTHS_FONDI = [
  152, // ISIN (wide enough for the FAM FoF expand toggle, issue #90)
  300, // Nome
  85, // PAC
  100, // Tipo prodotto
  80, // Valuta (display: Divisa)
  100, // Peso sul PTF % (display: Peso)
  110, // Controvalore
  100, // Final Score
  150, // Final Score Advice
  120, // P&L fiscale €
  120, // P&L fiscale %
  200, // Microcategoria Advice (display: Microcategoria)
  80, // YTM
  100, // Rating ESG
  90, // Duration
  150, // Commissioni di gestione
  100, // Abbuoni
  140, // Costi totali stimati
  120, // Dividend yield
  180, // Frequenza distribuzione dividendo
]; // 20 columns

// Column widths for obbligazionari
const BASE_COLUMN_WIDTHS_OBBLIGAZIONARI = [
  152, // ISIN (wide enough for the FAM FoF expand toggle, issue #90)
  300, // Nome
  80, // Valuta (display: Divisa)
  100, // Peso sul PTF % (display: Peso)
  110, // Controvalore
  120, // P&L fiscale €
  120, // P&L fiscale %
  200, // Microcategoria Advice (display: Microcategoria)
  90, // Duration
  100, // Rateo €
  80, // YTM
  100, // Rating ESG
  150, // Tipo emittente
  100, // Tipo bond
  100, // Paese
  110, // Scadenza
  120, // Dividend yield
  180, // Frequenza distribuzione cedole
]; // 18 columns

// Column widths for azioni
// Total must equal BASE_COLUMN_WIDTHS_OBBLIGAZIONARI total (2312) so first columns align
const BASE_COLUMN_WIDTHS_AZIONI = [
  152, // ISIN (wide enough for the FAM FoF expand toggle, issue #90)
  300, // Nome
  80, // Valuta (display: Divisa)
  180, // Controvalore
  167, // Peso sul PTF % (display: Peso)
  195, // P&L fiscale €
  195, // P&L fiscale %
  345, // Microcategoria Advice (display: Microcategoria)
  168, // Rating ESG
  195, // Dividend yield
  335, // Frequenza distribuzione dividendo
]; // 11 columns

// Column widths for certificati (Phase 3 — FIDA fields, no YTM/Duration)
// P&L fiscale columns are present in headers but hidden via HIDDEN_COLUMNS
// because the imported source does not provide P&L data for certificates.
const BASE_COLUMN_WIDTHS_CERTIFICATI = [
  152, // ISIN (wide enough for the FAM FoF expand toggle, issue #90)
  300, // Nome
  80, // Valuta (display: Divisa)
  110, // Controvalore
  80, // Peso sul PTF % (display: Peso)
  120, // P&L fiscale €  (hidden)
  120, // P&L fiscale %  (hidden)
  150, // Emittente
  150, // Microcategoria
  90, // Data emissione
  90, // Data scadenza
  80, // Costi totali (%)
  150, // Categoria Acepi
  120, // Tipologia
  80, // Protezione
  80, // Barriera down
  80, // Opzione quanto
  90, // Dividend Yield
]; // 18 columns (16 visible after hiding P&L)

// Column widths for polizze
const BASE_COLUMN_WIDTHS_POLIZZE = [
  152, // Codice prodotto (in place of ISIN)
  300, // Nome
  100, // Tipo prodotto
  80, // Valuta (display: Divisa)
  100, // Peso sul PTF % (display: Peso)
  110, // Controvalore
  200, // Microcategoria Advice (display: Microcategoria)
  80, // YTM
  100, // Rating ESG
  90, // Duration
  150, // Commissioni di gestione
  140, // Costi totali stimati
]; // 12 columns, as in the funds table

// Define sortable columns
const SORTABLE_COLUMNS_FONDI = [
  'peso_sul_ptf',
  'controvalore',
  'final_score',
  'final_score_advice',
  'pl_fiscale_euro',
  'pl_fiscale_perc',
  'microcategoria_advice',
  'ytm',
  'rating_ESG',
  'duration',
  'commissioni_gestione',
  'abbuoni',
  'costi_totali_stimati',
  'dividend_yield',
  'frequenza_distribuzione_dividendo',
];
const SORTABLE_COLUMNS_OBBLIGAZIONARI = [
  'peso_sul_ptf',
  'controvalore',
  'pl_fiscale_euro',
  'pl_fiscale_perc',
  'microcategoria_advice',
  'duration',
  'rateo_euro',
  'ytm',
  'rating_ESG',
  'tipo_emittente',
  'tipo_bond',
  'paese',
  'scadenza',
  'dividend_yield',
  'frequenza_distribuzione_dividendo',
];

const SORTABLE_COLUMNS_AZIONI = [
  'peso_sul_ptf',
  'pl_fiscale_euro',
  'pl_fiscale_perc',
  'microcategoria_advice',
  'rating_ESG',
  'dividend_yield',
  'frequenza_distribuzione_dividendo',
];

const SORTABLE_COLUMNS_CERTIFICATI = [
  'peso_sul_ptf',
  'controvalore',
  'emittente',
  'microcategoria_advice',
  'data_scadenza',
  'costi_totali_stimati',
  'fida_categoria_acepi',
  'fida_tipologia',
  'dividend_yield',
];

const SORTABLE_COLUMNS_POLIZZE = ['peso_sul_ptf', 'controvalore', 'microcategoria_advice', 'ytm', 'rating_ESG'];

// Static headers that always include controvalore
const HEADERS_FONDI = [
  'isin',
  'nome',
  'investment_type',
  'tipo_prodotto',
  'valuta',
  'peso_sul_ptf',
  'controvalore',
  'final_score',
  'final_score_advice',
  'pl_fiscale_euro',
  'pl_fiscale_perc',
  'microcategoria_advice',
  'ytm',
  'rating_ESG',
  'duration',
  'commissioni_gestione',
  'abbuoni',
  'costi_totali_stimati',
  'dividend_yield',
  'frequenza_distribuzione_dividendo',
];

const HEADERS_OBBLIGAZIONARI = [
  'isin',
  'nome',
  'valuta',
  'peso_sul_ptf',
  'controvalore',
  'pl_fiscale_euro',
  'pl_fiscale_perc',
  'microcategoria_advice',
  'duration',
  'rateo_euro',
  'ytm',
  'rating_ESG',
  'tipo_emittente',
  'tipo_bond',
  'paese',
  'scadenza',
  'dividend_yield',
  'frequenza_distribuzione_dividendo',
];

const HEADERS_AZIONI = [
  'isin',
  'nome',
  'valuta',
  'controvalore',
  'peso_sul_ptf',
  'pl_fiscale_euro',
  'pl_fiscale_perc',
  'microcategoria_advice',
  'rating_ESG',
  'dividend_yield',
  'frequenza_distribuzione_dividendo',
];

const HEADERS_CERTIFICATI = [
  'isin',
  'nome',
  'valuta',
  'controvalore',
  'peso_sul_ptf',
  'pl_fiscale_euro',
  'pl_fiscale_perc',
  'emittente',
  'microcategoria_advice',
  'data_emissione',
  'data_scadenza',
  'costi_totali_stimati',
  'fida_categoria_acepi',
  'fida_tipologia',
  'fida_protezione',
  'fida_barriera_down',
  'fida_quanto',
  'dividend_yield',
];

const HEADERS_POLIZZE = Object.keys(POLIZZE_COLUMN_LABELS);

// Column mappings for display labels
const COLUMN_MAPPINGS_FONDI = {
  isin: 'ISIN',
  nome: 'Nome',
  investment_type: 'Tipologia',
  tipo_prodotto: 'Tipo prodotto',
  valuta: 'Divisa',
  controvalore: 'Controvalore',
  peso_sul_ptf: 'Peso',
  final_score: 'Final Score',
  final_score_advice: 'Final Score Advice',
  pl_fiscale_euro: 'P&L fiscale €',
  pl_fiscale_perc: 'P&L fiscale %',
  microcategoria_advice: 'Microcategoria',
  ytm: 'YTM',
  rating_ESG: 'Rating ESG',
  duration: 'Duration',
  commissioni_gestione: 'Commissioni di gestione',
  abbuoni: 'Abbuoni',
  costi_totali_stimati: 'Costi totali stimati',
  dividend_yield: 'Dividend yield',
  frequenza_distribuzione_dividendo: 'Frequenza distribuzione dividendo',
};

const COLUMN_MAPPINGS_OBBLIGAZIONARI = {
  isin: 'ISIN',
  nome: 'Nome',
  valuta: 'Divisa',
  peso_sul_ptf: 'Peso',
  controvalore: 'Controvalore',
  pl_fiscale_euro: 'P&L fiscale €',
  pl_fiscale_perc: 'P&L fiscale %',
  microcategoria_advice: 'Microcategoria',
  duration: 'Duration',
  rateo_euro: 'Rateo €',
  ytm: 'YTM',
  rating_ESG: 'Rating ESG',
  tipo_emittente: 'Tipo emittente',
  tipo_bond: 'Tipo bond',
  paese: 'Paese',
  scadenza: 'Scadenza',
  dividend_yield: 'Dividend yield',
  frequenza_distribuzione_dividendo: 'Frequenza distribuzione cedole',
};

const COLUMN_MAPPINGS_AZIONI = {
  isin: 'ISIN',
  nome: 'Nome',
  tipo_prodotto: 'Tipo prodotto',
  valuta: 'Divisa',
  controvalore: 'Controvalore',
  peso_sul_ptf: 'Peso',
  pl_fiscale_euro: 'P&L fiscale €',
  pl_fiscale_perc: 'P&L fiscale %',
  microcategoria_advice: 'Microcategoria',
  rating_ESG: 'Rating ESG',
  final_score: 'Final Score',
  final_score_advice: 'Final Score Advice',
  dividend_yield: 'Dividend yield',
  frequenza_distribuzione_dividendo: 'Frequenza distribuzione dividendo',
};

const COLUMN_MAPPINGS_CERTIFICATI = {
  isin: 'ISIN',
  nome: 'Nome',
  valuta: 'Divisa',
  controvalore: 'Controvalore',
  peso_sul_ptf: 'Peso',
  pl_fiscale_euro: 'P&L fiscale €',
  pl_fiscale_perc: 'P&L fiscale %',
  emittente: 'Emittente',
  microcategoria_advice: 'Microcategoria',
  data_emissione: 'Data emissione',
  data_scadenza: 'Data scadenza',
  costi_totali_stimati: 'Costi totali (%)',
  fida_categoria_acepi: 'Categoria Acepi',
  fida_tipologia: 'Tipologia',
  fida_protezione: 'Protezione',
  fida_barriera_down: 'Barriera down',
  fida_quanto: 'Opzione quanto',
  dividend_yield: 'Dividend Yield',
};

const COLUMN_MAPPINGS_POLIZZE = POLIZZE_COLUMN_LABELS;

const EFFECTIVE_HIDDEN = new Set(HIDDEN_COLUMNS);

// Helper overrides using the effective hidden set
function filterColumnsLocal(headers: string[], widths: number[]) {
  const h: string[] = [], w: number[] = [];
  headers.forEach((header, i) => { if (!EFFECTIVE_HIDDEN.has(header)) { h.push(header); w.push(widths[i]); } });
  return { headers: h, widths: w };
}
function filterMappingsLocal<T extends Record<string, string>>(mappings: T): T {
  const result = { ...mappings };
  EFFECTIVE_HIDDEN.forEach((col) => delete (result as Record<string, string>)[col]);
  return result;
}

// Apply column filtering to all table configs
const FILTERED_OBBLIGAZIONARI = filterColumnsLocal(HEADERS_OBBLIGAZIONARI, BASE_COLUMN_WIDTHS_OBBLIGAZIONARI);
const FILTERED_AZIONI = filterColumnsLocal(HEADERS_AZIONI, BASE_COLUMN_WIDTHS_AZIONI);
const FILTERED_CERTIFICATI = filterColumnsLocal(HEADERS_CERTIFICATI, BASE_COLUMN_WIDTHS_CERTIFICATI);
const FILTERED_POLIZZE = filterColumnsLocal(HEADERS_POLIZZE, BASE_COLUMN_WIDTHS_POLIZZE);

const FILTERED_SORTABLE_OBBLIGAZIONARI = SORTABLE_COLUMNS_OBBLIGAZIONARI.filter((c) => !EFFECTIVE_HIDDEN.has(c));
const FILTERED_SORTABLE_AZIONI = SORTABLE_COLUMNS_AZIONI.filter((c) => !EFFECTIVE_HIDDEN.has(c));
const FILTERED_SORTABLE_CERTIFICATI = SORTABLE_COLUMNS_CERTIFICATI.filter((c) => !EFFECTIVE_HIDDEN.has(c));
const FILTERED_SORTABLE_POLIZZE = SORTABLE_COLUMNS_POLIZZE.filter((c) => !EFFECTIVE_HIDDEN.has(c));

const FILTERED_MAPPINGS_OBBLIGAZIONARI = filterMappingsLocal(COLUMN_MAPPINGS_OBBLIGAZIONARI);
const FILTERED_MAPPINGS_AZIONI = filterMappingsLocal(COLUMN_MAPPINGS_AZIONI);
const FILTERED_MAPPINGS_CERTIFICATI = filterMappingsLocal(COLUMN_MAPPINGS_CERTIFICATI);
const FILTERED_MAPPINGS_POLIZZE = filterMappingsLocal(COLUMN_MAPPINGS_POLIZZE);

interface ComposizioneDelPortfolioImportedProps {
  strumenti_fondi: ProductData[];
  strumenti_obbligazionari: ProductData[];
  strumenti_azioni: ProductData[];
  strumenti_certificati: ProductData[];
  strumenti_polizze: ProductData[];
  countervalue: CountervalueData;
}

const ComposizioneDelPortfolioImported = memo(
  ({
    strumenti_fondi,
    strumenti_obbligazionari,
    strumenti_azioni,
    strumenti_certificati,
    strumenti_polizze,
    countervalue,
  }: ComposizioneDelPortfolioImportedProps) => {
    const colorsConfig = colorsStore((state) => state.colorsConfigStore);
    const feeType = useFeeType();
    const constraintsPanelMode = activityPanelStore((state) => state.constraintsPanelMode);
    const isRebalancing = constraintsPanelMode !== 'builder';

    // Dynamic column hiding: fee-type-based FS column exclusion (Fondi only)
    const dynamicHiddenFondi = useMemo(() => {
      const hidden = new Set(EFFECTIVE_HIDDEN);
      if (isRebalancing && feeType === 'feeOnTop') hidden.add('final_score_advice');
      if (isRebalancing && feeType === 'feeOnly') hidden.add('final_score');
      return hidden;
    }, [isRebalancing, feeType]);

    const dynFilteredFondi = useMemo(() => {
      const h: string[] = [], w: number[] = [];
      HEADERS_FONDI.forEach((header, i) => { if (!dynamicHiddenFondi.has(header)) { h.push(header); w.push(BASE_COLUMN_WIDTHS_FONDI[i]); } });
      return { headers: h, widths: w };
    }, [dynamicHiddenFondi]);
    const dynSortableFondi = useMemo(() => SORTABLE_COLUMNS_FONDI.filter((c) => !dynamicHiddenFondi.has(c)), [dynamicHiddenFondi]);
    const dynMappingsFondi = useMemo(() => {
      const m = { ...COLUMN_MAPPINGS_FONDI };
      dynamicHiddenFondi.forEach((col) => delete (m as Record<string, string>)[col]);
      return m;
    }, [dynamicHiddenFondi]);

    // Transform fondi data to add formatted controvalore
    const transformedFondi = useMemo((): ProductData[] => {
      if (!strumenti_fondi || strumenti_fondi.length === 0) {
        return [];
      }

      return strumenti_fondi.map((asset) => {
        const transformed = { ...asset };
        transformed.investment_type = asset.investment_type === 'PAC' ? 'PAC' : 'PIC';

        // Format numeric fields to Italian locale
        const numericFields = [
          'controvalore',
          'peso_sul_ptf',
          'pl_fiscale_euro',
          'pl_fiscale_perc',
          'ytm',
          'duration',
          'commissioni_gestione',
          'abbuoni',
          'costi_totali_stimati',
          'dividend_yield',
        ];

        numericFields.forEach((field) => {
          if (asset[field] !== undefined && asset[field] !== null && asset[field] !== '-') {
            const value = Number(asset[field]);
            if (!isNaN(value)) {
              transformed[field] = formatItalianNumber(value);
            }
          } else {
            transformed[field] = '-';
          }
        });

        // Set default values for optional fields
        transformed.final_score = asset.final_score ? asset.final_score : '-';
        transformed.final_score_advice = asset.final_score_advice ? asset.final_score_advice : '-';
        transformed.rating_ESG = asset.rating_ESG ? asset.rating_ESG : '-';
        transformed.microcategoria_advice = asset.microcategoria_advice ? asset.microcategoria_advice : '-';
        transformed.frequenza_distribuzione_dividendo = asset.frequenza_distribuzione_dividendo
          ? asset.frequenza_distribuzione_dividendo
          : '-';

        return transformed;
      });
    }, [strumenti_fondi]);

    // Transform obbligazionari data to add formatted controvalore
    const transformedObbligazionari = useMemo((): ProductData[] => {
      if (!strumenti_obbligazionari || strumenti_obbligazionari.length === 0) {
        return [];
      }

      return strumenti_obbligazionari.map((asset) => {
        const transformed = { ...asset };

        // Format numeric fields to Italian locale
        const numericFields = [
          'controvalore',
          'peso_sul_ptf',
          'pl_fiscale_euro',
          'pl_fiscale_perc',
          'duration',
          'rateo_euro',
          'ytm',
          'dividend_yield',
        ];

        numericFields.forEach((field) => {
          if (asset[field] !== undefined && asset[field] !== null && asset[field] !== '-') {
            const value = Number(asset[field]);
            if (!isNaN(value)) {
              transformed[field] = formatItalianNumber(value);
            }
          } else {
            transformed[field] = '-';
          }
        });

        // Set default values for optional fields
        transformed.rating_ESG = asset.rating_ESG ? asset.rating_ESG : '-';
        transformed.microcategoria_advice = asset.microcategoria_advice ? asset.microcategoria_advice : '-';
        transformed.tipo_emittente = asset.tipo_emittente ? asset.tipo_emittente : '-';
        transformed.tipo_bond = asset.tipo_bond ? asset.tipo_bond : '-';
        transformed.paese = asset.paese ? asset.paese : '-';
        transformed.scadenza = asset.scadenza ? asset.scadenza : '-';
        transformed.frequenza_distribuzione_dividendo = asset.frequenza_distribuzione_dividendo
          ? asset.frequenza_distribuzione_dividendo
          : '-';

        return transformed;
      });
    }, [strumenti_obbligazionari]);

    // Transform azioni data to add formatted controvalore
    const transformedAzioni = useMemo((): ProductData[] => {
      if (!strumenti_azioni || strumenti_azioni.length === 0) {
        return [];
      }

      return strumenti_azioni.map((asset) => {
        const transformed = { ...asset };

        // Format numeric fields to Italian locale
        const numericFields = ['controvalore', 'peso_sul_ptf', 'pl_fiscale_euro', 'pl_fiscale_perc', 'dividend_yield'];

        numericFields.forEach((field) => {
          if (asset[field] !== undefined && asset[field] !== null && asset[field] !== '-') {
            const value = Number(asset[field]);
            if (!isNaN(value)) {
              transformed[field] = formatItalianNumber(value);
            }
          } else {
            transformed[field] = '-';
          }
        });

        // Set default values for optional fields
        transformed.rating_ESG = asset.rating_ESG ? asset.rating_ESG : '-';
        transformed.microcategoria_advice = asset.microcategoria_advice ? asset.microcategoria_advice : '-';
        transformed.final_score = asset.final_score ? asset.final_score : '-';
        transformed.final_score_advice = asset.final_score_advice ? asset.final_score_advice : '-';
        transformed.frequenza_distribuzione_dividendo = asset.frequenza_distribuzione_dividendo
          ? asset.frequenza_distribuzione_dividendo
          : '-';

        return transformed;
      });
    }, [strumenti_azioni]);

    // Transform certificati data to add formatted controvalore
    const transformedCertificati = useMemo((): ProductData[] => {
      if (!strumenti_certificati || strumenti_certificati.length === 0) {
        return [];
      }

      return strumenti_certificati.map((asset) => {
        const transformed = { ...asset };

        // Format numeric fields to Italian locale
        const numericFields = [
          'controvalore',
          'peso_sul_ptf',
          'pl_fiscale_euro',
          'pl_fiscale_perc',
          'costi_totali_stimati',
          'fida_protezione',
          'fida_barriera_down',
          'dividend_yield',
        ];

        numericFields.forEach((field) => {
          if (asset[field] !== undefined && asset[field] !== null && asset[field] !== '-') {
            const value = Number(asset[field]);
            if (!isNaN(value)) {
              transformed[field] = formatItalianNumber(value);
            }
          } else {
            transformed[field] = '-';
          }
        });

        // Set default values for optional string fields
        transformed.emittente = asset.emittente ? asset.emittente : '-';
        transformed.microcategoria_advice = asset.microcategoria_advice ? asset.microcategoria_advice : '-';
        transformed.data_emissione = asset.data_emissione ? asset.data_emissione : '-';
        transformed.data_scadenza = asset.data_scadenza ? asset.data_scadenza : '-';
        transformed.fida_categoria_acepi = asset.fida_categoria_acepi ? asset.fida_categoria_acepi : '-';
        transformed.fida_tipologia = asset.fida_tipologia ? asset.fida_tipologia : '-';
        transformed.fida_quanto = asset.fida_quanto ? asset.fida_quanto : '-';

        return transformed;
      });
    }, [strumenti_certificati]);

    // Transform polizze data to add formatted controvalore
    const transformedPolizze = useMemo((): ProductData[] => {
      if (!strumenti_polizze || strumenti_polizze.length === 0) {
        return [];
      }

      return strumenti_polizze.map((asset) => {
        const transformed = { ...asset };

        // Format numeric fields to Italian locale
        const numericFields = ['controvalore', 'peso_sul_ptf', 'ytm', 'duration', 'commissioni_gestione', 'costi_totali_stimati'];

        numericFields.forEach((field) => {
          if (asset[field] !== undefined && asset[field] !== null && asset[field] !== '-') {
            const value = Number(asset[field]);
            if (!isNaN(value)) {
              transformed[field] = formatItalianNumber(value);
            }
          } else {
            transformed[field] = '-';
          }
        });

        transformed.isin = asset.isin || '-';
        transformed.nome = asset.nome || '-';
        transformed.valuta = asset.valuta || '-';
        transformed.tipo_prodotto = polizzaTipoProdotto(asset);
        transformed.rating_ESG = asset.rating_ESG ? asset.rating_ESG : '-';
        transformed.microcategoria_advice = asset.microcategoria_advice ? asset.microcategoria_advice : '-';

        return transformed;
      });
    }, [strumenti_polizze]);

    const {
      sortConfig: sortConfigFondi,
      sortedData: sortedDataFondi,
      handleSort: handleSortFondi,
    } = useStrumentiImportedSort({
      data: transformedFondi,
      sortableColumns: dynSortableFondi,
    });
    const {
      sortConfig: sortConfigObbligazionari,
      sortedData: sortedDataObbligazionari,
      handleSort: handleSortObbligazionari,
    } = useStrumentiImportedSort({
      data: transformedObbligazionari,
      sortableColumns: FILTERED_SORTABLE_OBBLIGAZIONARI,
    });
    const {
      sortConfig: sortConfigAzioni,
      sortedData: sortedDataAzioni,
      handleSort: handleSortAzioni,
    } = useStrumentiImportedSort({
      data: transformedAzioni,
      sortableColumns: FILTERED_SORTABLE_AZIONI,
    });
    const {
      sortConfig: sortConfigCertificati,
      sortedData: sortedDataCertificati,
      handleSort: handleSortCertificati,
    } = useStrumentiImportedSort({
      data: transformedCertificati,
      sortableColumns: FILTERED_SORTABLE_CERTIFICATI,
    });
    const {
      sortConfig: sortConfigPolizze,
      sortedData: sortedDataPolizze,
      handleSort: handleSortPolizze,
    } = useStrumentiImportedSort({
      data: transformedPolizze,
      sortableColumns: FILTERED_SORTABLE_POLIZZE,
    });

    // These functions don't update component state directly
    const handleTooltipTrigger = useCallback((event: React.MouseEvent, text: string) => {
      showGlobalTooltip(event, text);
    }, []);

    const handleTooltipHide = useCallback(() => {
      hideGlobalTooltip();
    }, []);

    // Calculate subtotals for each category
    const fondiSubtotal = useMemo(() => {
      const subtotal = transformedFondi.reduce((sum, item) => {
        const value = parseFloat(item.controvalore.replace(/\./g, '').replace(',', '.')) || 0;
        return sum + value;
      }, 0);
      return formatItalianNumber(subtotal);
    }, [transformedFondi]);

    const obbligazionariSubtotal = useMemo(() => {
      const subtotal = transformedObbligazionari.reduce((sum, item) => {
        const value = parseFloat(item.controvalore.replace(/\./g, '').replace(',', '.')) || 0;
        return sum + value;
      }, 0);
      return formatItalianNumber(subtotal);
    }, [transformedObbligazionari]);

    const azioniSubtotal = useMemo(() => {
      const subtotal = transformedAzioni.reduce((sum, item) => {
        const value = parseFloat(item.controvalore.replace(/\./g, '').replace(',', '.')) || 0;
        return sum + value;
      }, 0);
      return formatItalianNumber(subtotal);
    }, [transformedAzioni]);

    const certificatiSubtotal = useMemo(() => {
      const subtotal = transformedCertificati.reduce((sum, item) => {
        const value = parseFloat(item.controvalore.replace(/\./g, '').replace(',', '.')) || 0;
        return sum + value;
      }, 0);
      return formatItalianNumber(subtotal);
    }, [transformedCertificati]);

    const polizzeSubtotal = useMemo(() => {
      const subtotal = transformedPolizze.reduce((sum, item) => {
        const value = parseFloat(item.controvalore.replace(/\./g, '').replace(',', '.')) || 0;
        return sum + value;
      }, 0);
      return formatItalianNumber(subtotal);
    }, [transformedPolizze]);

    const polizzeSubtitle = polizzeSubTitle(transformedPolizze);

    return (
      <div className={styles.complexTableContainer}>
        {sortedDataPolizze.length > 0 && (
          <div className={styles.tableSectionContainer}>
            <h4 className={styles.tableTitle}>{POLIZZE_TITLE}</h4>
            {polizzeSubtitle && <h5 className={styles.tableTitle}>{polizzeSubtitle}</h5>}
            <div className={styles.tableContainerInner}>
              <PortfolioTable
                data={sortedDataPolizze}
                headers={FILTERED_POLIZZE.headers}
                sortConfig={sortConfigPolizze}
                columnMappings={FILTERED_MAPPINGS_POLIZZE}
                columnWidths={FILTERED_POLIZZE.widths}
                colorsConfig={colorsConfig}
                handleSort={handleSortPolizze}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={FILTERED_SORTABLE_POLIZZE}
                pinnedColumns={['isin', 'nome']}
              />
              <GlobalTooltip />
            </div>

            <div className={styles.subtotalContainer}>
              <span className={styles.subtotalLabel}>{POLIZZE_SUBTOTAL_LABEL}</span>
              <span className={styles.subtotalValue}>€{polizzeSubtotal}</span>
            </div>
          </div>
        )}
        <div className={styles.tableSectionContainer}>
          <h4 className={styles.tableTitle}>Fondi, SICAV, ETF, ETC, ETN</h4>
          <div className={styles.tableContainerInner}>
            <PortfolioTable
              data={sortedDataFondi}
              headers={dynFilteredFondi.headers}
              sortConfig={sortConfigFondi}
              columnMappings={dynMappingsFondi}
              columnWidths={dynFilteredFondi.widths}
              colorsConfig={colorsConfig}
              handleSort={handleSortFondi}
              onTooltipTrigger={handleTooltipTrigger}
              onTooltipHide={handleTooltipHide}
              sortableColumns={dynSortableFondi}
              pinnedColumns={['isin', 'nome']}
            />
            <GlobalTooltip />
          </div>

          <div className={styles.subtotalContainer}>
            <span className={styles.subtotalLabel}>Totale controvalore portafoglio Fondi, SICAV, ETF, ETC, ETN</span>
            <span className={styles.subtotalValue}>€{fondiSubtotal}</span>
          </div>
        </div>
        {sortedDataObbligazionari.length > 0 && (
          <div className={styles.tableSectionContainer}>
            <h4 className={styles.tableTitle}>Titoli Obbligazionari</h4>
            <div className={styles.tableContainerInner}>
              <PortfolioTable
                data={sortedDataObbligazionari}
                headers={FILTERED_OBBLIGAZIONARI.headers}
                sortConfig={sortConfigObbligazionari}
                columnMappings={FILTERED_MAPPINGS_OBBLIGAZIONARI}
                columnWidths={FILTERED_OBBLIGAZIONARI.widths}
                colorsConfig={colorsConfig}
                handleSort={handleSortObbligazionari}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={FILTERED_SORTABLE_OBBLIGAZIONARI}
                pinnedColumns={['isin', 'nome']}
              />
              <GlobalTooltip />
            </div>

            <div className={styles.subtotalContainer}>
              <span className={styles.subtotalLabel}>Totale controvalore portafoglio Obbligazioni</span>
              <span className={styles.subtotalValue}>€{obbligazionariSubtotal}</span>
            </div>
          </div>
        )}
        {sortedDataAzioni.length > 0 && (
          <div className={styles.tableSectionContainer}>
            <h4 className={styles.tableTitle}>Titoli Azionari</h4>
            <div className={styles.tableContainerInner}>
              <PortfolioTable
                data={sortedDataAzioni}
                headers={FILTERED_AZIONI.headers}
                sortConfig={sortConfigAzioni}
                columnMappings={FILTERED_MAPPINGS_AZIONI}
                columnWidths={FILTERED_AZIONI.widths}
                colorsConfig={colorsConfig}
                handleSort={handleSortAzioni}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={FILTERED_SORTABLE_AZIONI}
                pinnedColumns={['isin', 'nome']}
              />
              <GlobalTooltip />
            </div>

            <div className={styles.subtotalContainer}>
              <span className={styles.subtotalLabel}>Totale controvalore portafoglio Azioni</span>
              <span className={styles.subtotalValue}>€{azioniSubtotal}</span>
            </div>
          </div>
        )}
        {sortedDataCertificati.length > 0 && (
          <div className={styles.tableSectionContainer}>
            <h4 className={styles.tableTitle}>Certificati</h4>
            <div className={styles.tableContainerInner}>
              <PortfolioTable
                data={sortedDataCertificati}
                headers={FILTERED_CERTIFICATI.headers}
                sortConfig={sortConfigCertificati}
                columnMappings={FILTERED_MAPPINGS_CERTIFICATI}
                columnWidths={FILTERED_CERTIFICATI.widths}
                colorsConfig={colorsConfig}
                handleSort={handleSortCertificati}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={FILTERED_SORTABLE_CERTIFICATI}
                pinnedColumns={['isin', 'nome']}
              />
              <GlobalTooltip />
            </div>

            <div className={styles.subtotalContainer}>
              <span className={styles.subtotalLabel}>Totale controvalore portafoglio Certificati</span>
              <span className={styles.subtotalValue}>€{certificatiSubtotal}</span>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default ComposizioneDelPortfolioImported;
